import type { TemplateAssociations } from "@amzn/d16-g-cdb-publisher-portal-typescript-client";
import type { AsyncStatus } from "@react-hookz/web";
import { useDebouncedEffect } from "@react-hookz/web";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useStore } from "zustand";

import { onDemandDecksFilters } from "../../config/filters/onDemandDeckTemplates";
import { FeatureFlagsStore } from "../../store/featureFlags";
import { discoveryFilterStore } from "../../store/filters";
import { IHubContentProvider } from "../../types/providers";
import { isLoading, isSuccess } from "../../utils/api.utils";
import { getFilterOptions } from "../../utils/filters.utils";
import {
    generateOnDemandInsightsFilterOptions,
    matchesOnDemandInsightSelections,
} from "../../utils/onDemandInsights.utils";
import { getAdvertiserId } from "../../utils/permission.utils";
import { logInsightBrowseResults } from "../../utils/telemetry.utils";
import { useClientSideLazyLoad } from "../misc/useClientSideLazyLoad";

import { useOnDemandInsightsAction } from "./useOnDemandInsightsAction";

declare global {
    interface Window {
        MyNamespace: any;
    }
}

window.MyNamespace = window.MyNamespace || {};

export interface OnDemandInsightsLoaderProps {
    isLoaderInView?: boolean;
}

export function useOnDemandInsightsLoader({
    isLoaderInView,
}: OnDemandInsightsLoaderProps): [AsyncStatus, TemplateAssociations[], number] {
    const [useUnifiedSearch] = useStore(FeatureFlagsStore, (state) => [
        state.unifiedSearch,
    ]);
    const [insights, setInsights] = useState<TemplateAssociations[]>([]);
    const [
        setFilterOptions,
        setData,
        fieldSelections,
        contentSubType,
        searchQuery,
    ] = useStore(discoveryFilterStore, (state) => [
        state.setFilterOptions,
        state.setData,
        state.getInsightsFilters().TEMPLATES.fieldSelections,
        state.getContentSubType(),
        state.getInsightsFilters()[IHubContentProvider.TEMPLATES].searchQuery,
    ]);
    const advertiserId = getAdvertiserId();
    const theme = contentSubType.scope;
    const [insightsState, insightsAction] = useOnDemandInsightsAction(
        advertiserId,
        theme,
    );
    const fetchOnDemandInsights = useCallback(async () => {
        setInsights([]);
        if (isLoading(insightsState.status)) return Promise.resolve();

        void insightsAction.execute().then((currentResponse) => {
            setInsights(currentResponse);
        });
    }, [insightsAction, setData, setInsights, insightsState]);

    useEffect(() => {
        void fetchOnDemandInsights();
    }, [contentSubType]);

    useEffect(() => {
        onDemandDecksFilters.forEach((filter) => {
            const currentMatchedInsights = insights.filter((insight) => {
                const currentFieldSelections = {
                    ...fieldSelections,
                    [filter.field]: [],
                };
                return matchesOnDemandInsightSelections(
                    insight,
                    currentFieldSelections,
                    searchQuery,
                );
            });
            const options = generateOnDemandInsightsFilterOptions(
                currentMatchedInsights,
            );
            setFilterOptions(
                filter.field,
                getFilterOptions(options[filter.field]),
            );
        });
    }, [insights, fieldSelections, searchQuery]);

    const matchedInsights = useMemo(() => {
        return insights.filter((insight) => {
            return matchesOnDemandInsightSelections(
                insight,
                fieldSelections,
                searchQuery,
            );
        });
    }, [insights, fieldSelections, searchQuery]);

    const [lazyLoadedInsights] = useClientSideLazyLoad(
        matchedInsights,
        isLoaderInView ?? false,
    );

    useDebouncedEffect(
        () => {
            if (isSuccess(insightsState.status)) {
                setData(IHubContentProvider.TEMPLATES, matchedInsights);
                logInsightBrowseResults(
                    matchedInsights.length,
                    useUnifiedSearch,
                );
            }
        },
        [matchedInsights, insightsState.status],
        500,
    );

    return [
        insightsState.status,
        lazyLoadedInsights as TemplateAssociations[],
        matchedInsights.length,
    ];
}
